import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { useLocation, useNavigate } from 'react-router-dom';
import NovaRegraModal from '../modal/NovaRegraModal';
import FotoDaRegraModal from '../modal/FotoDaRegraModal';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
// Faixa de erro compartilhada, usada pelo modal da fotografia.
import '../../../shared/feedback/erro-de-tela.css';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`). O import é explícito de propósito: depender de CSS carregado por
// outra tela é acoplamento invisível — foi o que originou o BUG-V74 na at-67.
import '../../recepcao/pages/Recepcao.css';
// Paleta de selos compartilhada (`cilia-badge` + badge-<cor>) — os selos da aba
// Personalizada eram estilo inline com 6 pares de cor próprios.
import '../../../shared/ui/badge-status.css';
import './regras.css';
import { listRegras, createRegra, deleteRegra, updateRegra, ModeloRegra, QualificadoTipo } from '../../../api/comissionamentosRegrasApi';
import PeriodoHeader from '../components/PeriodoHeader';
import { usePeriodoComissionamentos } from '../hooks/usePeriodoComissionamentos';
import { Esqueleto } from '../../../components/esqueleto/Esqueleto';
import DadosAtualizadosEm from '../components/DadosAtualizadosEm';
import AbasDoQualificado from '../components/AbasDoQualificado';
import PenIcon from '../../../img/pen.svg';
import ExcluirIcon from '../../../img/Excluir.svg';
// A câmera do sistema (a mesma dos modais de ações e das abas do checklist), não um ícone novo.
import CameraIcon from '../../../img/camera.svg';
import { notify } from '../../../shared/feedback/notify';

type TabRegra = 'vitalicio' | 'bonificacoes' | 'descontos' | 'personalizada';

interface Regra {
  id: string;
  modelo: TabRegra;
  observacoes: string;
  valor: number;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  contratoInicio?: string;
  contratoFim?: string | null;
  tipoRegra: 'PERCENTO' | 'VALOR';
  ativo: boolean;
  meta?: number;
  escopo?: 'LIDER' | 'EQUIPE' | 'AMBOS';
  alvoDebito?: 'PAI' | 'FILHO';
  fonteCalculo?: 'PAI' | 'FILHO' | 'AMBOS';
  operacao?: 'CREDITO' | 'DEBITO';
  multiplicador?: 'POR_BOLETO' | 'FIXO_MENSAL';
  aplicada?: {
    periodo?: string;
    qtd_entradas?: number;
    total_valor_pago?: string | number;
    valor_calculado?: string | number;
  } | null;
}

interface ComissionamentoItem {
  id: string;
  codigo: string;
  nome: string;
  contratos: number;
}

const mapAbaToModeloRegra = (t: TabRegra): ModeloRegra => {
  switch (t) {
    case 'vitalicio': return 'VITALICIO';
    case 'bonificacoes': return 'BONIFICAÇÕES';
    case 'descontos': return 'DESCONTOS';
    case 'personalizada': return 'PERSONALIZADA';
    default: return 'VITALICIO';
  }
};

const mapOrigemToQualificado = (o: 'regional' | 'consultor' | 'equipes' | 'cooperativa' | 'voluntario'): QualificadoTipo => {
  switch (o) {
    case 'regional': return 'REGIONAL';
    case 'consultor': return 'COOPERATIVA';
    case 'equipes': return 'VOLUNTARIO';
    case 'cooperativa': return 'COOPERATIVA';
    case 'voluntario': return 'VOLUNTARIO';
    default: return 'REGIONAL';
  }
};

const RegrasPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [item, setItem] = useState<ComissionamentoItem | null>(null);
  const [origem, setOrigem] = useState<'regional' | 'consultor' | 'equipes' | 'cooperativa' | 'voluntario' | null>(null);
  const [regras, setRegras] = useState<Regra[]>([]);
  // Regra aguardando confirmação de exclusão. Excluir apaga a regra e o repasse que ela gera
  // nas competências em que vale — não pode acontecer num clique só.
  const [regraParaExcluir, setRegraParaExcluir] = useState<Regra | null>(null);
  const [activeTab, setActiveTab] = useState<TabRegra>('vitalicio');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editInitialData, setEditInitialData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Fonte única do período do módulo: a tela ignorava o valor recebido na navegação
  // e voltava ao mês padrão (medido em 16/09/2026).
  const [periodo, setPeriodo] = usePeriodoComissionamentos();
  // Somas do período a partir de regras aplicadas
  /**
   * Fotografia da regra (at-04): qual regra e qual competência estão abertas no modal.
   * A competência é a do seletor da tela, mas o aviso de divergência pode pedir OUTRA —
   * por isso ela vai no estado, e não é lida direto de `periodo`.
   */
  const [fotoAberta, setFotoAberta] = useState<{ regraId: string; periodo: string; descricao?: string } | null>(null);
  const [sumEntradas, setSumEntradas] = useState<number>(0);
  const [sumValorPago, setSumValorPago] = useState<number>(0);
  const [sumValorRepasse, setSumValorRepasse] = useState<number>(0);
  const [sumAplicadas, setSumAplicadas] = useState<number>(0);

  // Util para formatar datas sem alterar dia por causa do timezone
  const formatDatePtBr = (iso?: string | null, emptyLabel: string = 'Sem fim') => {
    if (!iso) return emptyLabel;
    const d = iso.substring(0, 10); // YYYY-MM-DD
    const [y, m, dd] = d.split('-');
    if (!y || !m || !dd) return emptyLabel;
    return `${dd}/${m}/${y}`;
  };

  // Inicializa item e origem a partir da navegação
  useEffect(() => {
    if (location.state) {
      const st: any = location.state;
      if (st.item) setItem(st.item);
      if (st.origem) setOrigem(st.origem);
    }
  }, [location.state]);

  // Carrega regras sempre que item/origem/aba mudarem
  useEffect(() => {
    const ready = !!item && !!origem;
    if (!ready) return;
    loadRegras();
    // Justificativa do disable: o array abaixo já lista todos os valores reativos
    // que loadRegras lê (item, origem, activeTab e periodo), então o efeito
    // recarrega em todas as situações necessárias. Incluir a própria loadRegras
    // faria o efeito rodar a cada render — ela é recriada em todo render e chama
    // setState —, resultando em loop infinito de requisições.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, origem, activeTab, periodo]);

  // Resumo agregado do período (todas as abas)
  const normalizePeriodoToMMYYYY = (p?: string | null): string | null => {
    if (!p) return null;
    const s = String(p).trim();
    // Try YYYY-MM or YYYY/MM
    let m = s.match(/^(\d{4})[-/]?(\d{2})$/);
    if (m) return `${m[2]}-${m[1]}`;
    // Try MM-YYYY or MM/YYYY
    m = s.match(/^(\d{2})[-/]?(\d{4})$/);
    if (m) return `${m[1]}-${m[2]}`;
    // Try free-form: capture month and year anywhere
    const year = s.match(/\b(\d{4})\b/);
    const month = s.match(/\b(\d{1,2})\b/);
    if (year && month) return `${String(month[1]).padStart(2,'0')}-${year[1]}`;
    return null;
  };

  // Resumo por aba (somente a aba ativa)
  const computeResumoFromItems = (items: any[], periodoAtual: string) => {
    let entradas = 0;
    let valorPago = 0;
    let valorRepasse = 0;
    let aplicadasCount = 0;
    for (const it of items) {
      const ap = it?.aplicada;
      if (!ap) continue;
      const apPeriodo = normalizePeriodoToMMYYYY(ap?.periodo);
      if (apPeriodo && apPeriodo !== periodoAtual) continue;
      entradas += Number(ap?.qtd_entradas || 0);
      valorPago += Number(ap?.total_valor_pago || 0);
      valorRepasse += Number(ap?.valor_calculado || 0);
      aplicadasCount += 1;
    }
    setSumEntradas(entradas);
    setSumValorPago(valorPago);
    setSumValorRepasse(valorRepasse);
    setSumAplicadas(aplicadasCount);
  };

  const loadRegras = async () => {
    if (!item || !origem) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const modelo_regra = mapAbaToModeloRegra(activeTab);
      const qualificado_tipo = mapOrigemToQualificado(origem);
      const data = await listRegras({
        page: 1,
        limit: 20,
        modelo_regra,
        qualificado_tipo,
        qualificado_uuid: item.id,
        periodo,
      });
      const arr = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : data?.data || []);

      // Usa resumoPeriodo do backend para os totais do período
      const resumo = data?.resumoPeriodo;
      if (resumo) {
        setSumEntradas(Number(resumo.qtd_entradas ?? 0));
        setSumValorPago(Number(resumo.total_valor_pago ?? 0));
      } else {
        // Fallback: calcula somatórios da ABA ATIVA para o resumo
        computeResumoFromItems(arr, periodo);
      }

      // Calcula valor de repasse somando as regras aplicadas
      let valorRepasse = 0;
      let aplicadasCount = 0;
      for (const it of arr) {
        const ap = it?.aplicada;
        if (ap) {
          valorRepasse += Number(ap?.valor_calculado || 0);
          aplicadasCount += 1;
        }
      }
      setSumValorRepasse(valorRepasse);
      setSumAplicadas(aplicadasCount);

      const mapped: Regra[] = arr.map((r: any) => ({
        id: r.id,
        modelo: activeTab,
        observacoes: r.observacoes || '',
        valor: Number(r.valor) || 0,
        meta: Number(r.meta ?? 0),
        vigenciaInicio: r.validade_inicio,
        vigenciaFim: r.validade_fim ?? null,
        contratoInicio: r.contrato_inicio ?? '',
        contratoFim: r.contrato_fim ?? null,
        tipoRegra: (r.tipo_regra as 'PERCENTO' | 'VALOR') ?? 'VALOR',
        ativo: true,
        escopo: r.escopo ?? null,
        alvoDebito: r.alvo_debito ?? null,
        fonteCalculo: r.fonte_calculo ?? null,
        operacao: r.operacao ?? null,
        multiplicador: r.multiplicador ?? null,
        aplicada: r?.aplicada ?? null,
      }));
      setRegras(mapped);
    } catch (e: any) {
      console.error('Erro ao carregar regras:', e);
      setError(e?.message || 'Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  };

  const filteredRegras = useMemo(
    () => regras.filter((regra) => regra.modelo === activeTab),
    [regras, activeTab],
  );

  // Medição de colunas do padrão do sistema (mesmo hook da Recepção): mede o texto real,
  // distribui a sobra do container e soma o padding do casco. Sem isso o cabeçalho, que
  // tem `overflow: hidden`, era cortado antes da coluna Ações.
  const headers = ['Contrato', 'Vigência', 'Tipo', 'Valor', 'Entradas', 'Boletos pagos', 'Valor de repasse', 'Observação', 'Ações'];
  // Ações: três botões-ícone de 32px com gap de 8px = 112px, mais o respiro da célula. Os 100px
  // originais eram de quando havia dois botões — o terceiro ficava cortado (18/09/2026).
  const defaultColumnWidths = [150, 150, 90, 90, 80, 120, 130, 200, 130];
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tableRows = useMemo(
    () =>
      filteredRegras.map((regra) => [
        `${regra.contratoInicio ? formatDatePtBr(regra.contratoInicio, '—') : '—'} até ${regra.contratoFim ? formatDatePtBr(regra.contratoFim) : 'hoje'}`,
        `${formatDatePtBr(regra.vigenciaInicio)} até ${regra.vigenciaFim ? formatDatePtBr(regra.vigenciaFim) : 'hoje'}`,
        regra.tipoRegra === 'VALOR' ? 'Valor' : 'Percentual',
        `R$ ${regra.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        String(regra.aplicada?.qtd_entradas ?? 0),
        formatCurrency(Number(regra.aplicada?.total_valor_pago || 0)),
        formatCurrency(Number(regra.aplicada?.valor_calculado || 0)),
        // A observação é longa de propósito: a coluna corta com reticências, então a
        // medição usa um texto de referência em vez do texto inteiro.
        'Observação de referência para a medida',
        'Ações',
      ]),
    // formatDatePtBr é função do componente, estável entre renders no que importa aqui.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredRegras],
  );
  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: tableRows,
    remeasureKey: activeTab,
  });

  const handleVoltar = () => {
    navigate(-1);
  };

  const handleNovaRegra = () => {
    setEditInitialData(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleSaveRegra = async (novaRegra: any) => {
    if (!item || !origem) return;
    const body: any = {
      qualificado_tipo: mapOrigemToQualificado(origem),
      qualificado_uuid: item.id,
      modelo_regra: mapAbaToModeloRegra(activeTab),
      tipo_regra: novaRegra.tipoRegra, // 'PERCENTO' | 'VALOR'
      contrato_inicio: novaRegra.contratoInicio || undefined,
      contrato_fim: novaRegra.contratoFim || null,
      validade_inicio: novaRegra.validadeInicio,
      validade_fim: novaRegra.validadeFim || null,
      valor: Number(novaRegra.valor) || 0,
      meta: novaRegra.meta !== undefined ? Number(novaRegra.meta) : undefined,
      observacoes: novaRegra.observacoes || undefined,
    };
    // Campos exclusivos para PERSONALIZADA
    if (activeTab === 'personalizada') {
      body.escopo = novaRegra.escopo || 'AMBOS';
      body.alvo_debito = novaRegra.alvoDebito || 'PAI';
      body.operacao = novaRegra.operacao || 'CREDITO';
      body.multiplicador = novaRegra.multiplicador || 'POR_BOLETO';
    }
    // "Calcular sobre" vale para TODA regra de cooperativa (at-03), não só a Personalizada — é
    // ele que separa "somente líder" de "somente Voluntários dele". Enviar só na Personalizada
    // fazia a regra do Vitalício nascer sem a distinção e contar os dois conjuntos.
    if (mapOrigemToQualificado(origem) === 'COOPERATIVA') {
      body.fonte_calculo = novaRegra.fonteCalculo || 'AMBOS';
    }
    const editando = modalMode === 'edit' && Boolean(novaRegra.id);
    try {
      if (editando) {
        await updateRegra(novaRegra.id, body);
      } else {
        await createRegra(body);
      }
    } catch (e) {
      notify.errorFrom(
        e,
        editando ? 'Não foi possível salvar as alterações da regra.' : 'Não foi possível criar a regra.',
      );
      // Rejeitar é o que mantém o modal aberto com os dados digitados (ver NovaRegraModal.onSave).
      throw e;
    }
    // O modal se fecha sozinho depois que a promessa resolve; aqui só a lista recarrega.
    await loadRegras();
  };

  const handleDeleteRegra = async (id: string) => {
    try {
      await deleteRegra(id);
      await loadRegras();
    } catch (e) {
      notify.errorFrom(e, 'Não foi possível excluir a regra.');
    }
  };

  const confirmarExclusao = async () => {
    if (!regraParaExcluir) return;
    const id = regraParaExcluir.id;
    setRegraParaExcluir(null);
    await handleDeleteRegra(id);
  };

  const handleEditRegra = (regra: Regra) => {
    setModalMode('edit');
    setEditInitialData({
      id: regra.id,
      observacoes: regra.observacoes,
      contratoInicio: regra.contratoInicio || '',
      contratoFim: regra.contratoFim || '',
      tipoRegra: regra.tipoRegra,
      valor: regra.valor,
      meta: regra.meta ?? 0,
      validadeInicio: regra.vigenciaInicio,
      validadeFim: regra.vigenciaFim || '',
      escopo: regra.escopo ?? 'AMBOS',
      alvoDebito: regra.alvoDebito ?? 'PAI',
      fonteCalculo: regra.fonteCalculo ?? 'AMBOS',
      operacao: regra.operacao ?? 'CREDITO',
      multiplicador: regra.multiplicador ?? 'POR_BOLETO',
    });
    setIsModalOpen(true);
  };

  const getTabTitle = (tab: string) => {
    switch (tab) {
      case 'vitalicio': return 'Vitalício';
      case 'bonificacoes': return 'Bonificações';
      case 'descontos': return 'Descontos';
      case 'personalizada': return 'Personalizada';
      default: return tab;
    }
  };

  if (!item) {
    return (
      <div
        className="Frame1000004012"
        style={{
          height: 'fit-content',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          backgroundColor: 'transparent',
        }}
      >
        {/* Estado de "item não encontrado": mesmo esqueleto, sem cabeçalho — não há
            período nem navegação a oferecer, e o conteúdo exibido é o mesmo de antes. */}
        <div className="ConteDo recepcao-conte-do regras-conte-do">
          <div className="error-message">
            <h2>Item não encontrado</h2>
            <button onClick={handleVoltar} className="btn-voltar">
              Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      {/*
        Esqueleto COMPARTILHADO de página. `recepcao-conte-do` e `recepcao-header-row`
        substituem o contêiner `.regras-container`, que tinha margens próprias e não
        acompanhava as demais telas. A classe local abaixo guarda só o respiro inferior.
      */}
      <div className="ConteDo recepcao-conte-do regras-conte-do">
        {/*
          O carregamento não esconde mais a tela inteira (era um texto solto no lugar de
          tudo, 16/09/2026): cabeçalho, "Voltar", período, abas e navegação continuam
          montados; só o resumo e a tabela viram esqueleto.
        */}
            {/* Header com período e botão voltar. `regras-header` ficou só com a faixa
                laranja de separação — o resto do cabeçalho vem do esqueleto compartilhado. */}
            <div className="recepcao-header-row regras-header">
              <button onClick={handleVoltar} className="btn-back">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                Voltar
              </button>
              <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Regras de Comissionamento
              </div>
              {/* Período encostado à direita, como o `flex: 1` do título antigo fazia. */}
              <div style={{ marginLeft: 'auto' }}>
                <PeriodoHeader periodo={periodo} onChange={setPeriodo} />
              </div>
            </div>
            <DadosAtualizadosEm />

            {/* Regras / Contratos / Pagamentos como ABAS (16/09/2026) — antes eram três
                botões de navegação copiados nas três telas. */}
            <AbasDoQualificado
              ativa="regras"
              estado={{
                item,
                origem,
                qualificado_tipo: origem ? mapOrigemToQualificado(origem) : 'COOPERATIVA',
                qualificado_uuid: item?.id,
              }}
              // O aviso de divergência abre a fotografia da regra com MAIOR repasse da
              // competência apontada — é a que mais pesa na diferença, e o modal permite
              // trocar de aba para ver as outras.
              onAbrirCompetencia={(p) => {
                const alvo = [...regras].sort(
                  (a, b) => Number(b.aplicada?.valor_calculado || 0) - Number(a.aplicada?.valor_calculado || 0),
                )[0];
                if (alvo) setFotoAberta({ regraId: alvo.id, periodo: p, descricao: alvo.observacoes });
              }}
            />

        {/* Resumo dos dados do item */}
        <div className="item-resumo">
          <div className="resumo-card">
            <h2>Resumo do Período</h2>
            <div className="resumo-dados">
              <div className="dado-item">
                <span className="label">Código:</span>
                <span className="valor">{item.codigo}</span>
              </div>
              <div className="dado-item">
                <span className="label">Nome:</span>
                <span className="valor">{item.nome}</span>
              </div>
              <div className="dado-item">
                <span className="label">Período:</span>
                <span className="valor">{periodo.replace('-', '/')}</span>
              </div>
              <div className="dado-item">
                <span className="label">Entradas:</span>
                <span className="valor">{loading ? <Esqueleto largura="60px" /> : sumEntradas}</span>
              </div>
              <div className="dado-item">
                <span className="label">Boletos pagos:</span>
                <span className="valor">
                  {loading ? <Esqueleto largura="110px" /> : formatCurrency(sumValorPago)}
                </span>
              </div>
              <div className="dado-item">
                <span className="label">Valor de repasse:</span>
                <span className="valor">
                  {loading ? <Esqueleto largura="110px" /> : formatCurrency(sumValorRepasse)}
                </span>
              </div>
              <div className="dado-item">
                <span className="label">Regras aplicadas:</span>
                <span className="valor">
                  {loading ? <Esqueleto largura="40px" /> : <span className="badge">{sumAplicadas}</span>}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Navegação por abas */}
        <div className="regras-tabs">
          <div className="tabs-header">
            {(['vitalicio', 'bonificacoes', 'descontos', 'personalizada'] as const).map((tab) => (
              <button
                key={tab}
                className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {getTabTitle(tab)}
              </button>
            ))}
          </div>
        </div>

        {/* Botão para nova regra */}
        <div className="regras-actions">
          <button onClick={handleNovaRegra} className="btn-nova-regra">
            + Nova Regra
          </button>
        </div>

        {/* Tabela de regras */}
        <div className="regras-table-container">
          {error && <div className="error-message">{error}</div>}
          {/*
            Casco COMPARTILHADO de tabela (`TabelaScroll`/`TabelaContent`/`Tabela`/`Item`/
            `Data`, de Recepcao.css) — o mesmo da lista e da Recepção.

            ⚠️ Cada regra são DOIS filhos do `TabelaContent`: a linha da grade (5 células) e
            uma faixa de detalhe própria (`regras-detalhe`), fora do grid. A primeira versão
            pôs os blocos DENTRO da linha com `gridColumn: 1 / -1`, e o resultado foi a
            tabela amassada — linha e detalhe são modelos diferentes e não cabem na mesma
            grade.
          */}
          <div className="regras-tabela-card">
            <div className="TabelaScroll" ref={tableScrollRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: tableColumns,
                  width: tableWidth,
                  minWidth: tableWidth,
                }}
                role="status"
                aria-busy={loading}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headers.map((titulo, index) => (
                        <div
                          key={titulo}
                          className="Opcoes"
                          ref={(el) => {
                            headerRefs.current[index] = el;
                          }}
                        >
                          {titulo}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {loading ? (
                  <>
                    <span className="esqueleto-sr-only">Carregando as regras</span>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <React.Fragment key={i}>
                        <div className="Tabela" aria-hidden="true">
                          <div className="Item"><div className="Data"><Esqueleto largura="140px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="140px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="70px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="70px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="50px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="100px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="100px" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="80%" /></div></div>
                          <div className="Item"><div className="Data"><Esqueleto largura="56px" altura="24px" /></div></div>
                        </div>
                      </React.Fragment>
                    ))}
                  </>
                ) : filteredRegras.length === 0 ? (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: '1 / -1' }}>
                      <div className="Data">
                        Nenhuma regra de {getTabTitle(activeTab).toLowerCase()} vale em {periodo.replace('-', '/')}.
                        Regras de outros meses não aparecem aqui: troque o período no cabeçalho para vê-las.
                      </div>
                    </div>
                  </div>
                ) : (
                  filteredRegras.map((regra) => (
                    <React.Fragment key={regra.id}>
                      <div className="Tabela">
                        <div className="Item">
                          <div className="Data">
                            {regra.contratoInicio ? formatDatePtBr(regra.contratoInicio, '—') : '—'}
                            {' '}até{' '}
                            {regra.contratoFim ? formatDatePtBr(regra.contratoFim) : 'hoje'}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">
                            {formatDatePtBr(regra.vigenciaInicio)}{' '}até{' '}
                            {regra.vigenciaFim ? formatDatePtBr(regra.vigenciaFim) : 'hoje'}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">{regra.tipoRegra === 'VALOR' ? 'Valor' : 'Percentual'}</div>
                        </div>
                        <div className="Item">
                          <div className="Data regras-valor">
                            {regra.tipoRegra === 'VALOR'
                              ? `R$ ${regra.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : `${regra.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                          </div>
                        </div>
                        {/* Os três números do período viraram COLUNAS (16/09/2026) — eram
                            cards numa faixa abaixo da linha. */}
                        <div className="Item">
                          <div className="Data">{Number(regra.aplicada?.qtd_entradas || 0).toLocaleString('pt-BR')}</div>
                        </div>
                        <div className="Item">
                          <div className="Data">{formatCurrency(Number(regra.aplicada?.total_valor_pago || 0))}</div>
                        </div>
                        <div className="Item">
                          <div className="Data regras-valor">
                            {formatCurrency(Number(regra.aplicada?.valor_calculado || 0))}
                          </div>
                        </div>
                        {/* Célula de UMA linha: texto longo corta com reticências e o valor
                            inteiro fica no `title` (regra do Victor, 12/08/2026). */}
                        <div className="Item">
                          <div className="Data regras-observacao" title={regra.observacoes || undefined}>
                            {regra.observacoes || '—'}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data regras-acoes">
                            {/* Fotografia: o que esta regra gerou quando nasceu × o que vale
                                hoje (at-04). Texto e não ícone porque a ação não tem símbolo
                                óbvio, e um ícone novo entre os dois existentes confundiria. */}
                            <button
                              className="btn-icon regras-btn-foto"
                              title="Ver a fotografia desta regra na competência"
                              onClick={() =>
                                setFotoAberta({ regraId: regra.id, periodo, descricao: regra.observacoes })
                              }
                            >
                              <img src={CameraIcon} alt="Fotografia" />
                            </button>
                            <button className="btn-icon btn-editar" title="Editar" onClick={() => handleEditRegra(regra)}>
                              <img src={PenIcon} alt="Editar" />
                            </button>
                            <button className="btn-icon btn-excluir" title="Excluir" onClick={() => setRegraParaExcluir(regra)}>
                              <img src={ExcluirIcon} alt="Excluir" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* A faixa de detalhe sobrou só para os selos da aba Personalizada —
                          números do período e observação viraram colunas (16/09/2026). */}
                      {activeTab === 'personalizada' && (
                        <div className="regras-detalhe">
                          <div className="regras-detalhe-selos">
                            <span className={`cilia-badge ${regra.operacao === 'DEBITO' ? 'badge-vermelho' : 'badge-verde'}`}>
                              {regra.operacao === 'DEBITO' ? 'Débito' : 'Crédito'}
                            </span>
                            <span className="cilia-badge badge-cinza">
                              {regra.multiplicador === 'FIXO_MENSAL' ? 'Fixo mensal' : 'Por boleto'}
                            </span>
                            <span className="cilia-badge badge-azul">
                              Aplica: {regra.escopo === 'LIDER' ? 'Líder' : regra.escopo === 'EQUIPE' ? 'Equipe' : 'Ambos'}
                            </span>
                            <span className="cilia-badge badge-laranja">
                              Credita: {regra.alvoDebito === 'FILHO' ? 'Voluntário' : 'Cooperativa'}
                            </span>
                            <span className="cilia-badge badge-cinza">
                              Sobre: {regra.fonteCalculo === 'FILHO' ? 'Equipe' : regra.fonteCalculo === 'AMBOS' ? 'Todos' : 'Líder'}
                            </span>
                            {(regra.meta ?? 0) > 0 && (
                              <span className="cilia-badge badge-laranja">Meta: {regra.meta} adesões</span>
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <NovaRegraModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveRegra}
          activeTab={activeTab}
          initialData={editInitialData || undefined}
          qualificadoTipo={origem ? mapOrigemToQualificado(origem) : 'COOPERATIVA'}
          mode={modalMode}
        />

        {/* Montagem condicional, não prop `isOpen`: é o padrão adotado no projeto para o
            estado do modal nascer do `useState` a cada abertura (at-61). */}
        {regraParaExcluir && (
          <ConfirmModal
            isOpen
            title="Excluir esta regra?"
            message={
              `A regra ${descreverRegra(regraParaExcluir)} deixa de valer para este qualificado ` +
              'e o repasse que ela gera sai do cálculo no próximo processamento. A fotografia ' +
              'das competências já calculadas é preservada. Esta ação não pode ser desfeita por aqui.'
            }
            confirmLabel="Excluir regra"
            onConfirm={confirmarExclusao}
            onCancel={() => setRegraParaExcluir(null)}
          />
        )}

        {fotoAberta && (
          <FotoDaRegraModal
            regraId={fotoAberta.regraId}
            periodo={fotoAberta.periodo}
            descricaoDaRegra={fotoAberta.descricao}
            onClose={() => setFotoAberta(null)}
          />
        )}
      </div>
    </div>
  );
};

const formatCurrency = (n: number) => {
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/** Como a regra aparece na confirmação de exclusão: o valor dela e, se houver, a observação. */
const descreverRegra = (regra: Regra) => {
  const valor =
    regra.tipoRegra === 'PERCENTO'
      ? `de ${regra.valor.toLocaleString('pt-BR')}%`
      : `de ${formatCurrency(regra.valor)}`;
  const observacao = regra.observacoes?.trim();
  return observacao ? `${valor} ("${observacao}")` : valor;
};

export default RegrasPage;